import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { createFileRoute } from '@tanstack/react-router';
import { useToast } from '@chakra-ui/react';
import axios from 'axios';
import { differenceInYears, format, parse } from 'date-fns';
import { Action, ItemType } from '@/../../server/types/loggingTypes';
import logo from '@/assets/logo.png';
import Button from '@/components/lib/Button';
import Input from '@/components/lib/Input';
import { setAuthInfo } from '@/store/slices/authSlice';
import { formatBirthdayInput } from '@/utils/birthday';
import { getStatsigClient } from '@/utils/statsig';

export const Route = createFileRoute('/_privateRoutes/onboarding/add-display-name-and-birthday')({
  component: AddDisplayNameAndBirthday,
});

export default function AddDisplayNameAndBirthday() {
  useEffect(() => {
    getStatsigClient().logEvent('add_display_name_and_birthday_page', Action.VIEW);
  }, []);

  const [displayName, setDisplayName] = useState('');
  const [birthday, setBirthday] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const toast = useToast();
  const maxDisplayNameLength = 20;

  const handleDisplayNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!displayName) {
        getStatsigClient().logEvent('add_display_name_and_birthday_page', Action.TYPING, {
          item_name: 'display_name',
        });
      }
      const newName = e.target.value;
      if (newName.length <= maxDisplayNameLength) {
        setDisplayName(newName);
      }
    },
    [displayName],
  );

  const handleBirthdayChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatBirthdayInput(e.target.value);
    setBirthday(formatted);
  }, []);

  const handleSubmit = async () => {
    getStatsigClient().logEvent('add_display_name_and_birthday_page', Action.CLICK, {
      item_name: 'submit',
      item_type: ItemType.BUTTON,
    });

    if (!displayName.trim()) {
      toast({
        title: 'Error',
        description: 'Display name cannot be empty.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const birthRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
    if (!birthRegex.test(birthday)) {
      toast({
        title: 'Error',
        description: 'Birthday must be in MM/DD/YYYY format.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const parsed = parse(birthday, 'MM/dd/yyyy', new Date());
    if (!parsed || isNaN(parsed.getTime())) {
      toast({
        title: 'Error',
        description: 'Invalid birthday.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (differenceInYears(new Date(), parsed) < 13) {
      toast({
        title: 'Age Restriction',
        description: 'Sorry, you need to be at least 13 years old to use our product.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);

    try {
      const displayNameRes = await axios.put('/api/user/updateDisplayName', {
        displayName: displayName.trim(),
      });
      if (displayNameRes.status !== 200) {
        throw new Error('Failed to update display name.');
      }

      const dateOfBirth = format(parsed, 'yyyy-MM-dd');
      const birthdayRes = await axios.put('/api/user/updateDateOfBirth', { dateOfBirth });
      if (birthdayRes.status !== 200) {
        throw new Error('Failed to update date of birth');
      }

      dispatch(setAuthInfo({ displayName: displayName.trim(), dateOfBirth }));
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update info',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isLoading) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="flex flex-col items-center min-h-screen bg-[#121212] px-4 pb-6 relative">
      {/* Header */}
      <div className="relative flex flex-row items-center justify-center py-3 px-4 w-full">
        <img src={logo} alt="Bond Logo" className="h-8 object-contain" />
      </div>

      <h1 className="text-white text-[34px]/[40px] tracking-tight text-center max-w-[280px] font-bold mt-8 mb-4">
        Enter your name and birthday
      </h1>
      <p className="text-white/60 text-[16px]/[20px] tracking-tight text-center max-w-[280px] mb-8">
        You need to be at least 13 years old to use Bond
      </p>

      <div className="w-full max-w-[334px] flex flex-col items-center gap-4">
        <Input
          placeholder="Full Name"
          value={displayName}
          onChange={handleDisplayNameChange}
          onKeyDown={handleKeyDown}
          size="lg"
        />
        <Input
          placeholder="MM/DD/YYYY"
          value={birthday}
          onChange={handleBirthdayChange}
          onKeyDown={handleKeyDown}
          size="lg"
          inputMode="numeric"
          pattern="[0-9]*"
        />
        <Button className="mt-4" size="lg" isLoading={isLoading} onClick={handleSubmit}>
          Next
        </Button>
      </div>
    </div>
  );
}
